// src/services/supabaseAdminOptimized.ts
// Version optimisée sans RLS lourd pour de meilleures performances

import { supabase } from './supabaseClient';
import type {
    AdminUser,
    MemberData,
    DashboardStats
} from '../types/admin.types';

// Types optimisés

// Interface spécifique pour les données d'export (plus légère)
export interface MemberExportData {
    member_number: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone: string;
    province: string;
    city: string;
    profession?: string;
    gender?: string;
    referrals_count: number;
    status: 'active' | 'inactive';
    created_at: string;
}

export interface ApiError {
    message?: string;
    error?: string;
    details?: string;
}

export interface ApiResponse<T> {
    data: T | null;
    error: ApiError | null;
}

export interface PaginatedResponse<T> {
    data: T[];
    count: number;
    totalPages: number;
    currentPage: number;
    error: ApiError | null;
}

// Imports des types

// Cache pour les permissions (éviter les appels répétés)
let permissionsCache: { [userId: string]: AdminUser } = {};
let cacheExpiry: { [userId: string]: number } = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Service d'authentification optimisé
export class AdminAuthService {
    static async signIn(email: string, password: string) {
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (error) return { data: null, error };

            if (data.user) {
                // Vérifier l'accès admin
                const adminInfo = await this.getAdminInfo(data.user.id);
                if (!adminInfo) {
                    await supabase.auth.signOut();
                    return {
                        data: null,
                        error: { message: 'Accès non autorisé' }
                    };
                }

                // Mettre à jour la dernière connexion en arrière-plan
                this.updateLastLogin(data.user.id).catch(console.error);
            }

            return { data, error };
        } catch (error) {
            return { data: null, error };
        }
    }

    static async signOut() {
        // Nettoyer le cache
        const { data: currentUser } = await supabase.auth.getUser();
        if (currentUser.user) {
            delete permissionsCache[currentUser.user.id];
            delete cacheExpiry[currentUser.user.id];
        }

        return await supabase.auth.signOut();
    }

    static async getCurrentUser() {
        return await supabase.auth.getUser();
    }

    static async getAdminInfo(userId?: string): Promise<AdminUser | null> {
        const { data: currentUser } = await this.getCurrentUser();
        const targetUserId = userId || currentUser.user?.id;
        if (!targetUserId) return null;

        // Vérifier le cache
        const now = Date.now();
        if (
            permissionsCache[targetUserId] &&
            cacheExpiry[targetUserId] &&
            now < cacheExpiry[targetUserId]
        ) {
            return permissionsCache[targetUserId];
        }

        try {
            const { data, error } = await supabase.rpc('get_admin_user_info', {
                user_id: targetUserId
            });

            if (error || !data || data.length === 0) {
                return null;
            }

            const adminInfo = data[0] as AdminUser;

            // Mettre en cache
            permissionsCache[targetUserId] = adminInfo;
            cacheExpiry[targetUserId] = now + CACHE_DURATION;

            return adminInfo;
        } catch (error) {
            console.error('Erreur lors de la récupération des infos admin:', error);
            return null;
        }
    }

    static async updateLastLogin(userId: string) {
        try {
            await supabase
                .from('admin_users')
                .update({ last_login: new Date().toISOString() })
                .eq('id', userId);
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la dernière connexion:', error);
        }
    }

    static onAuthStateChange(callback: (user: any) => void) {
        return supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_OUT') {
                // Nettoyer le cache à la déconnexion
                permissionsCache = {};
                cacheExpiry = {};
            }
            callback(session?.user || null);
        });
    }

    // Vérifications rapides de permissions (utilise le cache)
    static async checkPermission(permission: 'read' | 'write' | 'delete' | 'manage_users'): Promise<boolean> {
        const adminInfo = await this.getAdminInfo();
        if (!adminInfo) return false;

        switch (permission) {
            case 'read': return adminInfo.can_read;
            case 'write': return adminInfo.can_write;
            case 'delete': return adminInfo.can_delete;
            case 'manage_users': return adminInfo.can_manage_users;
            default: return false;
        }
    }

    // Invalidation manuelle du cache (si besoin)
    static clearPermissionsCache(userId?: string) {
        if (userId) {
            delete permissionsCache[userId];
            delete cacheExpiry[userId];
        } else {
            permissionsCache = {};
            cacheExpiry = {};
        }
    }
}

// Service de gestion des membres optimisé
export class MemberManagementService {
    // Dashboard stats avec cache côté client
    private static dashboardCache: { data: DashboardStats; timestamp: number } | null = null;
    private static readonly DASHBOARD_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

    static async getDashboardStats(useCache: boolean = true): Promise<ApiResponse<DashboardStats>> {
        // Vérifier le cache
        if (useCache && this.dashboardCache) {
            const now = Date.now();
            if (now - this.dashboardCache.timestamp < this.DASHBOARD_CACHE_DURATION) {
                return { data: this.dashboardCache.data, error: null };
            }
        }

        try {
            const { data, error } = await supabase.rpc('get_admin_dashboard_stats');

            if (error) throw error;

            const statsData = data?.[0] || null;

            // Mettre en cache
            if (statsData) {
                this.dashboardCache = {
                    data: statsData,
                    timestamp: Date.now()
                };
            }

            return { data: statsData, error: null };
        } catch (error: any) {
            return { data: null, error: { message: error.message || 'Erreur lors du chargement des statistiques' } };
        }
    }

    // Recherche optimisée avec gestion d'erreurs
    static async searchMembers(
        searchQuery?: string,
        provinceFilter?: string,
        statusFilter?: string,
        page: number = 1,
        pageSize: number = 20
    ): Promise<PaginatedResponse<MemberData>> {
        try {
            const offset = (page - 1) * pageSize;

            const { data, error } = await supabase.rpc('admin_search_members', {
                search_query: searchQuery || null,
                province_filter: provinceFilter || null,
                status_filter: statusFilter || null,
                limit_count: pageSize,
                offset_count: offset
            });

            if (error) throw error;

            const totalCount = data?.[0]?.total_count || 0;
            const members = data || [];

            return {
                data: members as MemberData[],
                count: totalCount,
                totalPages: Math.ceil(totalCount / pageSize),
                currentPage: page,
                error: null
            };
        } catch (error: any) {
            console.error('Erreur lors de la recherche de membres:', error);
            return {
                data: [],
                count: 0,
                totalPages: 0,
                currentPage: page,
                error: { message: error.message || 'Erreur lors de la recherche de membres' }
            };
        }
    }

    // Récupérer un membre par ID (avec cache simple)
    private static memberCache: { [id: string]: { data: MemberData; timestamp: number } } = {};

    static async getMemberById(id: string, useCache: boolean = false): Promise<ApiResponse<MemberData>> {
        // Cache simple pour éviter les requêtes répétées
        if (useCache && this.memberCache[id]) {
            const cached = this.memberCache[id];
            if (Date.now() - cached.timestamp < 60000) { // 1 minute
                return { data: cached.data, error: null };
            }
        }

        try {
            const { data, error } = await supabase
                .from('members')
                .select('*')
                .eq('id', id)
                .single();

            if (error) throw error;

            // Mettre en cache
            if (data) {
                this.memberCache[id] = {
                    data: data as MemberData,
                    timestamp: Date.now()
                };
            }

            return { data: data as MemberData, error: null };
        } catch (error: any) {
            return { data: null, error: { message: error.message || 'Erreur lors de la récupération du membre' } };
        }
    }

    // Mise à jour optimisée
    static async updateMember(id: string, updates: Partial<MemberData>): Promise<ApiResponse<any>> {
        try {
            const { data, error } = await supabase.rpc('admin_update_member', {
                member_id: id,
                updates: updates
            });

            if (error) throw error;

            // Invalider le cache
            delete this.memberCache[id];
            this.dashboardCache = null; // Invalider le cache du dashboard

            return { data, error: null };
        } catch (error: any) {
            return { data: null, error: { message: error.message || 'Erreur lors de la mise à jour du membre' } };
        }
    }

    // Suppression optimisée
    static async deleteMember(id: string): Promise<ApiResponse<any>> {
        try {
            const { data, error } = await supabase.rpc('admin_delete_member', {
                member_id: id
            });

            if (error) throw error;

            // Invalider les caches
            delete this.memberCache[id];
            this.dashboardCache = null;

            return { data, error: null };
        } catch (error: any) {
            return { data: null, error: { message: error.message || 'Erreur lors de la suppression du membre' } };
        }
    }

    // Statistiques par province (utilise la vue optimisée)
    static async getProvinceStats() {
        try {
            const { data, error } = await supabase
                .from('admin_top_provinces')
                .select('*');

            return { data: data || [], error };
        } catch (error) {
            return { data: [], error };
        }
    }

    // Top parrains (utilise la vue optimisée)
    static async getTopReferrers(limit: number = 10) {
        try {
            const { data, error } = await supabase
                .from('admin_top_referrers')
                .select('*')
                .limit(limit);

            return { data: data || [], error };
        } catch (error) {
            return { data: [], error };
        }
    }

    // Membres récents optimisé
    static async getRecentMembers(limit: number = 10) {
        try {
            const { data, error } = await supabase
                .from('members')
                .select('id, member_number, first_name, last_name, city, province, created_at')
                .eq('status', 'active')
                .order('created_at', { ascending: false })
                .limit(limit);

            return { data: data || [], error };
        } catch (error) {
            return { data: [], error };
        }
    }

    // Export optimisé par batches
    static async exportMembers(filters?: {
        province?: string;
        status?: string;
        dateFrom?: string;
        dateTo?: string;
    }): Promise<ApiResponse<MemberExportData[]>> {
        try {
            let query = supabase
                .from('members')
                .select('member_number,first_name,last_name,email,phone,province,city,profession,gender,referrals_count,status,created_at');

            // Appliquer les filtres
            if (filters?.province) {
                query = query.eq('province', filters.province);
            }
            if (filters?.status) {
                query = query.eq('status', filters.status);
            }
            if (filters?.dateFrom) {
                query = query.gte('created_at', filters.dateFrom);
            }
            if (filters?.dateTo) {
                query = query.lte('created_at', filters.dateTo);
            }

            // Ordonner et limiter si nécessaire (éviter les exports trop gros)
            const { data, error } = await query
                .order('created_at', { ascending: false })
                .limit(10000); // Limite raisonnable

            if (error) throw error;

            return { data: (data as MemberExportData[]) || [], error: null };
        } catch (error: any) {
            return { data: [], error: { message: error.message || 'Erreur lors de l\'export des membres' } };
        }
    }

    // Nettoyer les caches manuellement
    static clearCaches() {
        this.dashboardCache = null;
        this.memberCache = {};
    }
}

// Fonctions utilitaires pour les hooks (à importer dans les composants React)
export const createAdminAuthHook = () => {
    // Cette fonction retourne les fonctions à utiliser dans un hook React personnalisé
    return {
        checkUser: async () => {
            try {
                const { data: { user: authUser } } = await AdminAuthService.getCurrentUser();
                if (authUser) {
                    return await AdminAuthService.getAdminInfo(authUser.id);
                }
                return null;
            } catch (error) {
                console.error('Erreur lors de la vérification de l\'utilisateur:', error);
                return null;
            }
        },

        onAuthChange: (callback: (user: AdminUser | null) => void) => {
            return AdminAuthService.onAuthStateChange(async (authUser) => {
                if (authUser) {
                    const adminInfo = await AdminAuthService.getAdminInfo(authUser.id);
                    callback(adminInfo);
                } else {
                    callback(null);
                }
            });
        }
    };
};

// Fonction utilitaire pour vérifier les permissions
export const checkUserPermissions = (user: AdminUser | null) => {
    return {
        canRead: user?.can_read || false,
        canWrite: user?.can_write || false,
        canDelete: user?.can_delete || false,
        canManageUsers: user?.can_manage_users || false,
        isAdmin: user?.role === 'admin',
        isViewer: user?.role === 'viewer',
        hasAccess: !!user?.can_read,
        user: user
    };
};

// Factory pour créer un composant protégé (à utiliser dans les composants React)
export const createProtectedComponent = (
    requiredPermission: 'read' | 'write' | 'delete' | 'manage_users'
) => {
    return {
        checkPermission: (user: AdminUser | null) => {
            const permissions = checkUserPermissions(user);
            const permissionMap = {
                read: permissions.canRead,
                write: permissions.canWrite,
                delete: permissions.canDelete,
                manage_users: permissions.canManageUsers
            };
            return permissionMap[requiredPermission];
        },
        requiredPermission
    };
};

const adminServices = {
    AdminAuthService,
    MemberManagementService
};

export default adminServices;
